import React, { memo, useState, useRef, useEffect } from 'react';
import { useTableContext } from '../../context/TableContext';
import { CampaignItemIcon } from '../../../../Sidebar/icons';
import ConfirmModal from '../../../FlowchartView/components/ConfirmModal';
import IconPicker from '../../../shared/IconPicker';
import { renderIcon } from '../../../FlowchartView/utils/renderIcon';
import { QuestMarker } from '../../../FlowchartView/components/FlowchartHUD/icons';
import DecisionOptionsModal, { DEFAULT_OPTIONS } from '../../../FlowchartView/TaskNode/DecisionOptionsModal';

/**
 * Name cell with inline editing
 * Memoized to prevent re-renders when other cells change
 */
const NameCell = memo(({ row, value }) => {
  const { onRenameTask, nestedSprints, lockStates, onOpenNestedSprint, onRemoveNestedSprint, onIconChange, onDecisionAnswerChange, onDecisionOptionsChange, columnVisibility } = useTableContext();
  
  // Check if icons should be shown (defaults to true if not set)
  const showIcon = columnVisibility?.icon !== false;
  const task = row.original;
  const isDecision = task.nodeType === 'decision';
  
  // Lock & excluded state
  const lockState = lockStates?.[task.id];
  const isLocked = lockState?.isLocked && task.status !== 'completed';
  const isExcluded = lockState?.isExcluded;
  
  // Decision state
  const options = task.options || DEFAULT_OPTIONS;
  const answer = task.answer || null;
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  
  // Find nested sprint for this task
  const nestedSprint = task.nestedSprintId 
    ? nestedSprints?.find(s => s.id === task.nestedSprintId)
    : null;

  // For tasks with nested campaigns, use the nested campaign's icon instead of task.icon
  // This creates a bidirectional sync: changing the icon here changes the nested campaign's icon
  const effectiveIcon = nestedSprint?.icon ?? task.icon;
  const hasIcon = !!effectiveIcon;

  // Check if this is a placeholder/empty name (needs naming)
  const isPlaceholderName = !value || value === 'ENTER MISSION NAME' || value === 'New Decision';
  const placeholderHint = isDecision ? 'Type a decision question...' : 'Type a mission name...';

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);
  const autoStartedForTaskRef = useRef(null); // Track which task ID we've auto-started for
  
  // Icon picker state
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerPosition, setIconPickerPosition] = useState(null);
  const iconRef = useRef(null);
  
  // Delete nested sprint confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing) {
      // Use requestAnimationFrame to ensure the textarea is rendered in the DOM
      const rafId = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.style.height = 'auto';
          inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
          // Place cursor at end
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isEditing]);

  // Auto-start editing when task has default placeholder name (newly created)
  useEffect(() => {
    // Only auto-start if this is a new task we haven't auto-started for yet
    if (isPlaceholderName && !isEditing && autoStartedForTaskRef.current !== task.id) {
      autoStartedForTaskRef.current = task.id;
      // Start editing with empty value - the focus effect will handle focusing the input
      setEditValue('');
      setIsEditing(true);
    }
  }, [task.id, isPlaceholderName, isEditing]);

  const handleStartEdit = (e) => {
    // If clicking on placeholder name, clear it and start fresh
    if (isPlaceholderName) {
      setEditValue('');
    } else {
      setEditValue(value);
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed !== value) {
      // Save whatever the user entered (including empty string)
      onRenameTask?.(task.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  // Icon picker handlers
  const handleIconClick = (e) => {
    e.stopPropagation();
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setIconPickerPosition({
        x: rect.left,
        y: rect.bottom + 8,
      });
    }
    setShowIconPicker(true);
  };

  const handleIconChange = (newIcon) => {
    onIconChange?.(task.id, newIcon);
    // Don't close picker - let user set both icon and color
  };

  // Decision handlers
  const handleAnswerClick = (optionId) => (e) => {
    e.stopPropagation();
    onDecisionAnswerChange?.(task.id, optionId === answer ? null : optionId);
  };

  const handleOptionsChange = (newOptions) => {
    onDecisionOptionsChange?.(task.id, newOptions);
    // If current answer is no longer valid, clear it
    if (answer && !newOptions.find(opt => opt.id === answer)) {
      onDecisionAnswerChange?.(task.id, null);
    }
  };

  if (isEditing) {
    return (
      <div className="table-view__name-edit-container">
        {/* Hint shown when editing and field is empty */}
        {editValue === '' && (
          <span className="table-view__name-hint">💡 {placeholderHint}</span>
        )}
        <textarea
          ref={inputRef}
          className="table-view__name-textarea"
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          rows={1}
        />
      </div>
    );
  }

  return (
    <div className="table-view__name-cell">
      {/* Main row with name */}
      <div className="table-view__name-wrapper">
        {/* Task icon - clickable to change (hidden if icon visibility is off) */}
        {showIcon && (
          <button
            ref={iconRef}
            className={`table-view__task-icon ${hasIcon ? 'table-view__task-icon--has-icon' : ''}`}
            onClick={handleIconClick}
            title={hasIcon ? "Change icon" : "Add icon"}
          >
            {hasIcon ? (
              renderIcon(effectiveIcon, { 
                size: '16px',
                fallback: null
              })
            ) : (
              <QuestMarker size={16} />
            )}
          </button>
        )}
        {isDecision && <span className="table-view__decision-badge">🔀</span>}
        <span
          className={`table-view__name ${isPlaceholderName ? 'table-view__name--placeholder' : ''}`}
          onClick={handleStartEdit}
          title="Click to edit"
        >
          {value || (isDecision ? 'Untitled decision' : 'Untitled mission')}
        </span>
        
        {isExcluded ? (
          <div 
            className="table-view__excluded-indicator"
            title="This path was not chosen — a different decision branch was selected"
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="table-view__excluded-icon"
            >
              <circle cx="12" cy="12" r="9" />
              <line x1="5.5" y1="5.5" x2="18.5" y2="18.5" />
            </svg>
          </div>
        ) : isLocked ? (
          <div 
            className="table-view__lock-indicator"
            title={lockState?.lockReason || 'This mission is locked until conditions are met'}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="table-view__lock-icon"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        ) : null}
      </div>
      
      {/* Decision Toggle - compact version for table view */}
      {isDecision && (
        <div className="table-view__decision-toggle">
          {options.map((option) => (
            <button
              key={option.id}
              className={`table-view__decision-btn ${answer === option.id ? 'table-view__decision-btn--active' : ''}`}
              style={{ '--option-color': option.color }}
              onClick={handleAnswerClick(option.id)}
              title={option.label}
            >
              {option.label}
            </button>
          ))}
          <button
            className="table-view__decision-add-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowOptionsModal(true);
            }}
            title="Add or edit choices"
          >
            +
          </button>
        </div>
      )}
      
      {/* Nested Sprint Indicator - on its own row below the name */}
      {nestedSprint && (
        <div className="table-view__nested-sprint-container">
          <span className="table-view__nested-sprint-header">NESTED CAMPAIGN:</span>
          <button
            className="table-view__nested-sprint"
            onClick={(e) => {
              e.stopPropagation();
              onOpenNestedSprint?.(nestedSprint);
            }}
            title={`Open nested campaign: ${nestedSprint.name}`}
          >
            {/* Left column: icon */}
            <span className="table-view__nested-sprint-icon">
              <CampaignItemIcon size={12} />
            </span>
            
            {/* Middle column: name + progress bar */}
            <div className="table-view__nested-sprint-middle">
              <span className="table-view__nested-sprint-name">{nestedSprint.name}</span>
              <div className="table-view__nested-sprint-progress">
                <div 
                  className={`table-view__nested-sprint-progress-fill${(nestedSprint.progress || 0) === 100 ? ' table-view__nested-sprint-progress-fill--complete' : ''}`}
                  style={{ width: `${nestedSprint.progress || 0}%` }}
                />
              </div>
            </div>
            
            {/* Right column: delete button */}
            <span
              className="table-view__nested-sprint-delete"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              title="Move to trash"
              role="button"
              tabIndex={0}
            >
              ×
            </span>
          </button>
        </div>
      )}
      
      {/* Confirmation modal for trashing nested sprint */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Move to Trash"
        message={`Move "${nestedSprint?.name}" to trash? The mission itself stays — it just won't contain a nested campaign anymore. You can restore the campaign later from the Trash.`}
        confirmLabel="Move to Trash"
        cancelLabel="Cancel"
        confirmVariant="warning"
        icon="🗑"
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onRemoveNestedSprint?.(task.id);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      
      {/* Icon Picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={effectiveIcon}
          onIconChange={handleIconChange}
          onIconDeleted={() => handleIconChange(null)}
          onClose={() => setShowIconPicker(false)}
          position={iconPickerPosition}
        />
      )}
      
      {/* Decision Options Modal */}
      {showOptionsModal && (
        <DecisionOptionsModal
          isOpen={showOptionsModal}
          options={options}
          onOptionsChange={handleOptionsChange}
          onClose={() => setShowOptionsModal(false)}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevProps.value === nextProps.value &&
         prevTask.id === nextTask.id &&
         prevTask.icon === nextTask.icon &&
         prevTask.nodeType === nextTask.nodeType &&
         prevTask.nestedSprintId === nextTask.nestedSprintId &&
         prevTask.answer === nextTask.answer &&
         prevTask.options === nextTask.options;
});

export default NameCell;

